import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import RouteMeta from "@/components/RouteMeta";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import VerifyEmailGate from "@/components/VerifyEmailGate";
import Layout from "@/components/Layout";
import DailyLudus from "@/pages/DailyLudus";
import Journey from "@/pages/Journey";
import Dashboard from "@/pages/Dashboard";
import Roster from "@/pages/Roster";
import Recruit from "@/pages/Recruit";
import Training from "@/pages/Training";
import Inbox from "@/pages/Inbox";
import Challenges from "@/pages/Challenges";
import CommunityEvents from "@/pages/CommunityEvents";
import Leaderboard from "@/pages/Leaderboard";
import Vip from "@/pages/Vip";
import Chat from "@/pages/Chat";
import Profile from "@/pages/Profile";
import GladiatorProfile from "@/pages/GladiatorProfile";
import SkillTree from "@/pages/SkillTree";
import SkillTreeManager from "@/pages/SkillTreeManager";
import News from "@/pages/News";
import Alliance from "@/pages/Alliance";
import AllianceWarRoom from "@/pages/AllianceWarRoom";
import AllianceArmoryPage from "@/pages/AllianceArmoryPage";
import AllianceWars from "@/pages/AllianceWars";
import Admin from "@/pages/Admin";
import AdminAlerts from "@/pages/AdminAlerts";
import PromoCodesAdmin from "@/pages/admin/PromoCodes";
import TournamentAdmin from "@/pages/admin/TournamentAdmin";
import MerchantManager from "@/pages/admin/MerchantManager";
import BeastRegistry from "@/pages/admin/BeastRegistry";
import MiningConfig from "@/pages/admin/MiningConfig";
import TournamentIntegrity from "@/pages/admin/TournamentIntegrity";
import LudusTriasAdmin from "@/pages/admin/LudusTriasAdmin";
import WheelFortunaAdmin from "@/pages/admin/WheelFortunaAdmin";
import LudusCustomize from "@/pages/LudusCustomize";
import AllianceCustomize from "@/pages/AllianceCustomize";
import Landing from "@/pages/Landing";
import PublicGladiator from "@/pages/PublicGladiator";
import PublicBattle from "@/pages/PublicBattle";
import PublicUpdates from "@/pages/PublicUpdates";
import Campaign from "@/pages/Campaign";
import Vivarium from "@/pages/Vivarium";
import Inventory from "@/pages/Inventory";
import DoctoreSanctumPage from "@/pages/DoctoreSanctumPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Contact from "@/pages/Contact";
import TermsOfService from "@/pages/TermsOfService";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import Guide from "@/pages/Guide";
import Strategy from "@/pages/Strategy";
import Hall from "@/pages/Hall";
import ArenaCircuitDashboard from "@/pages/ArenaCircuit/ArenaCircuitDashboard";
import CreateFighter from "@/pages/ArenaCircuit/CreateFighter";
import MyFighters from "@/pages/ArenaCircuit/MyFighters";
import FighterProfile from "@/pages/ArenaCircuit/FighterProfile";
import ArenaCircuitTraining from "@/pages/ArenaCircuit/Training";
import ArenaTrainingSummary from "@/pages/ArenaCircuit/TrainingSummary";
import BrowseCircuits from "@/pages/ArenaCircuit/BrowseCircuits";
import CreateCircuit from "@/pages/ArenaCircuit/CreateCircuit";
import CircuitProfile from "@/pages/ArenaCircuit/CircuitProfile";
import MyCircuit from "@/pages/ArenaCircuit/MyCircuit";
import ContractOffers from "@/pages/ArenaCircuit/ContractOffers";
import FightOffers from "@/pages/ArenaCircuit/FightOffers";
import RecruitFighters from "@/pages/ArenaCircuit/RecruitFighters";
import GoldHistory from "@/pages/GoldHistory";
import UniqueAnalytics from "@/pages/UniqueAnalytics";
import Mining from "@/pages/Mining";
import MiningJournal from "@/pages/MiningJournal";
import MiningRewards from "@/pages/MiningRewards";
import MiningRankings from "@/pages/MiningRankings";
import MiningSmelting from "@/pages/MiningSmelting";
import MiningSmithing from "@/pages/MiningSmithing";
import LudusTrias from "@/pages/TriumvirateArena";
import TriumvirateOpponents from "@/pages/TriumvirateOpponents";
import TriumvirateLadder from "@/pages/TriumvirateLadder";
import TriumvirateMatches from "@/pages/TriumvirateMatches";
import LudusTriasArchive from "@/pages/LudusTriasArchive";
import WikiHome from "@/pages/WikiHome";
import WikiCategory from "@/pages/WikiCategory";
import WikiArticle from "@/pages/WikiArticle";
import WikiSearch from "@/pages/WikiSearch";
import WikiAdmin from "@/pages/admin/WikiAdmin";

const Equipment = lazy(() => import("@/pages/Equipment"));
const HouseGames = lazy(() => import("@/pages/HouseGames"));

function Spinner() {
  return <div className="min-h-screen flex items-center justify-center text-amber-500 font-cinzel">Entering the arena…</div>;
}

const withRouteFallback = (element) => <Suspense fallback={<Spinner />}>{element}</Suspense>;

function ProfileRedirect() {
  const { user } = useAuth();
  return <Navigate to={`/profile/${user.user_id}`} replace />;
}

function RootGate() {
  const { user, loading } = useAuth();
  const loc = useLocation();
  
  // Show spinner while auth is loading
  if (loading || user === null) return <Spinner />;
  
  // For homepage: show Landing page if not logged in
  if (loc.pathname === "/" && !user) {
    return <Landing />;
  }
  
  // For all other scenarios: require authentication and show Layout
  if (!user) return <Navigate to="/" replace />;
  // Email verification is now optional - removed gate, user gets incentive message instead
  return <Layout />;
}

function AppRouter() {
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("lanista_ref", ref.trim());
  }, []);
  return (
    <Routes>
      {/* Public routes - always accessible */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/google" element={<Navigate to="/login" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/g/:id" element={<PublicGladiator />} />
      <Route path="/b/:id" element={<PublicBattle />} />
      <Route path="/updates" element={<PublicUpdates />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/about" element={<About />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/guide" element={<Guide />} />
      <Route path="/strategy" element={<Strategy />} />
      <Route path="/hall" element={<Hall />} />
      
      {/* Auth-gated routes */}
      <Route path="/" element={<RootGate />}>
        <Route index element={<DailyLudus />} />
        <Route path="journey" element={<Journey />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="roster" element={<Roster />} />
        <Route path="recruit" element={<Recruit />} />
        <Route path="training" element={<Training />} />
        <Route path="equipment" element={withRouteFallback(<Equipment />)} />
        <Route path="mining" element={<Mining />} />
        <Route path="mining/rewards" element={<MiningRewards />} />
        <Route path="mining/journal" element={<MiningJournal />} />
        <Route path="mining/rankings" element={<MiningRankings />} />
        <Route path="mining/smelting" element={<MiningSmelting />} />
        <Route path="mining/smithing" element={<MiningSmithing />} />
        <Route path="triumvirate" element={<Navigate to="/ludus-trias" replace />} />
        <Route path="triumvirate/opponents" element={<Navigate to="/ludus-trias/opponents" replace />} />
        <Route path="triumvirate/matches" element={<Navigate to="/ludus-trias/matches" replace />} />
        <Route path="triumvirate/ladder" element={<Navigate to="/ludus-trias/ladder" replace />} />
        <Route path="ludus-trias" element={<LudusTrias />} />
        <Route path="ludus-trias/opponents" element={<TriumvirateOpponents />} />
        <Route path="ludus-trias/matches" element={<TriumvirateMatches />} />
        <Route path="ludus-trias/ladder" element={<TriumvirateLadder />} />
        <Route path="ludus-trias/archive" element={<LudusTriasArchive />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="challenges" element={<Challenges />} />
        <Route path="campaign" element={<Campaign />} />
        <Route path="vivarium" element={<Vivarium />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="gold-history" element={<GoldHistory />} />
        <Route path="sanctum" element={<DoctoreSanctumPage />} />
        <Route path="arena-circuit" element={<ArenaCircuitDashboard />} />
        <Route path="arena-circuit/create-fighter" element={<CreateFighter />} />
        <Route path="arena-circuit/fighters" element={<MyFighters />} />
        <Route path="arena-circuit/fighter/:fighterId" element={<FighterProfile />} />
        <Route path="arena-circuit/training" element={<ArenaCircuitTraining />} />
        <Route path="arena-circuit/training-summary" element={<ArenaTrainingSummary />} />
        <Route path="arena-circuit/circuits" element={<BrowseCircuits />} />
        <Route path="arena-circuit/create-circuit" element={<CreateCircuit />} />
        <Route path="arena-circuit/circuit/:circuitId" element={<CircuitProfile />} />
        <Route path="arena-circuit/my-circuit" element={<MyCircuit />} />
        <Route path="arena-circuit/contract-offers" element={<ContractOffers />} />
        <Route path="arena-circuit/fight-offers" element={<FightOffers />} />
        <Route path="arena-circuit/recruit" element={<RecruitFighters />} />
        <Route path="events" element={<CommunityEvents />} />
        <Route path="house-games" element={withRouteFallback(<HouseGames />)} />
        <Route path="leaderboard" element={<Leaderboard />} />
        <Route path="vip" element={<Vip />} />
        <Route path="news" element={<News />} />
        <Route path="wiki" element={<WikiHome />} />
        <Route path="wiki/search" element={<WikiSearch />} />
        <Route path="wiki/:categorySlug" element={<WikiCategory />} />
        <Route path="wiki/:categorySlug/:articleSlug" element={<WikiArticle />} />
        <Route path="alliance" element={<Alliance />} />
        <Route path="alliance/war-room" element={<AllianceWarRoom />} />
        <Route path="alliance/armory" element={<AllianceArmoryPage />} />
        <Route path="alliance-wars" element={<AllianceWars />} />
        <Route path="alliance/:allianceId/warroom" element={<AllianceWarRoom />} /> {/* Admin access to any alliance's war room */}
        <Route path="alliance/customize" element={<AllianceCustomize />} />
        <Route path="alliance/:allianceId" element={<Alliance />} />
        <Route path="admin" element={<Admin />} />
        <Route path="admin/alerts" element={<AdminAlerts />} />
        <Route path="admin/promo-codes" element={<PromoCodesAdmin />} />
        <Route path="admin/tournaments" element={<TournamentAdmin />} />
        <Route path="admin/merchant" element={<MerchantManager />} />
        <Route path="admin/beasts" element={<BeastRegistry />} />
        <Route path="admin/mining" element={<MiningConfig />} />
        <Route path="admin/wiki" element={<WikiAdmin />} />
        <Route path="admin/tournament-integrity" element={<TournamentIntegrity />} />
        <Route path="admin/ludus-trias" element={<LudusTriasAdmin />} />
        <Route path="admin/wheel-fortuna" element={<WheelFortunaAdmin />} />
        <Route path="unique" element={<UniqueAnalytics />} />
        <Route path="chat" element={<Chat />} />
        <Route path="profile" element={<ProfileRedirect />} />
        <Route path="profile/:userId" element={<Profile />} />
        <Route path="customize" element={<LudusCustomize />} />
        <Route path="gladiator/:gladId" element={<GladiatorProfile />} />
        <Route path="gladiator/:gladId/skills" element={<SkillTree />} />
        <Route path="skill-tree-manager" element={<SkillTreeManager />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App arena-bg">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <RouteMeta />
            <AppRouter />
            <Toaster theme="dark" position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}
